import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import {} from '../../../schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions } from '../../../util/index.js';
import { EventLogResources, addEventLog } from '../../util.js';
import { createDrugItemCategories, createMedicationProfile, createMedicationProfileReconstitutionDiluent, createMedicationStabilityRule, } from './create.js';
import { deleteMedicationProfile, deleteMedicationProfileReconstitutionDiluent, deleteMedicationProfileStabilityRule, } from './delete.js';
import { updateMedicationProfile, updateMedicationProfileStabilityRule } from './update.js';
export async function replaceDrugItemCategories(tx, { drugItem, newDrugCategoriesIds }, userActions) {
    const currentDrugCategoriesIds = new Set(drugItem.categories?.map(c => c.drugCategoryId));
    const newCategoriesIds = new Set(newDrugCategoriesIds);
    const addedDrugCategories = newDrugCategoriesIds?.filter(c => !currentDrugCategoriesIds.has(c)) || [];
    const deletedDrugCategories = drugItem.categories?.filter(c => {
        // remove category if new categories doesn't include it
        if (!newCategoriesIds.has(c.drugCategoryId))
            return true;
        // remove child if parent was removed
        if (c.drugCategory?.parentId && !newCategoriesIds.has(c.drugCategory.parentId))
            return true;
        return false;
    }) || [];
    const userActionsTimestamp = pickTimestampFromUserActions(userActions);
    const userActionsActor = pickActorFromUserActions(userActions);
    if (addedDrugCategories.length) {
        await createDrugItemCategories(tx, { drugItemId: drugItem.id, categories: addedDrugCategories }, { createdAt: userActionsTimestamp, createdBy: userActionsActor });
    }
    if (deletedDrugCategories.length) {
        const relationsToMarkAsDeleted = deletedDrugCategories.flatMap(c => {
            const updatedRelation = {
                id: c.id,
                deletedAt: userActionsTimestamp,
                deletedBy: userActionsActor,
            };
            const promises = [tx.mutate.drugCatalogItemsCategories.update(updatedRelation)];
            if (tx.location === 'server') {
                const patches = createPatch(c, updatedRelation);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.drugCatalogItemsCategory,
                    resourceId: updatedRelation.id,
                    action: 'delete',
                    patches: patches,
                    performedBy: userActionsActor,
                    performedAt: userActionsTimestamp,
                    status: 'INFO',
                }));
            }
            return promises;
        });
        await Promise.all(relationsToMarkAsDeleted);
    }
    // drugItem.categories may include already removed links (not every by-id query filters them)
    const hasCategories = addedDrugCategories.length > 0 ||
        Boolean(drugItem.categories?.some(c => !c.deletedAt && !deletedDrugCategories.includes(c)));
    if (hasCategories !== drugItem.hasCategories) {
        await tx.mutate.drugCatalogItems.update({ id: drugItem.id, hasCategories });
    }
}
export async function replaceMedicationProfiles(tx, { newMedicationProfiles, drugItem, }, userActions) {
    const prevMedicationProfiles = drugItem.medicationProfiles || [];
    const updatedMedicationProfiles = newMedicationProfiles?.filter(item => Boolean(item.id)) || []; // an updated medication profile is one which already has an ID
    const updatedMedicationProfilesIds = new Set(updatedMedicationProfiles.map(item => item.id));
    const addedMedicationProfiles = newMedicationProfiles?.filter(item => !item.id) || []; // a new medication profile is one which doesn't have an ID
    const deletedMedicationProfiles = prevMedicationProfiles?.filter(item => !updatedMedicationProfilesIds.has(item.id)) || [];
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    if (addedMedicationProfiles.length) {
        await Promise.all(addedMedicationProfiles.map(profile => createMedicationProfile(tx, { ...profile, drugItemId: drugItem.id }, { createdAt: timestamp, createdBy: actor })));
    }
    if (updatedMedicationProfiles.length) {
        await Promise.all(updatedMedicationProfiles.map(profile => updateMedicationProfile(tx, { ...profile, drugItemId: drugItem.id }, { updatedAt: timestamp, updatedBy: actor })));
    }
    if (deletedMedicationProfiles.length) {
        await Promise.all(deletedMedicationProfiles.map(profile => deleteMedicationProfile(tx, { medicationProfile: profile }, { deletedAt: timestamp, deletedBy: actor })));
    }
}
export async function replaceMedicationProfileStabilityRules(tx, { medicationProfile, newStabilityRules, }, userActions) {
    const prevMedicationStabilityRules = medicationProfile.stabilityRules || [];
    const updatedStabilityRules = newStabilityRules?.filter(item => Boolean(item.id)) || []; // an updated resource is one which already has an ID
    const updatedStabilityRulesIds = new Set(updatedStabilityRules.map(item => item.id));
    const addedStabilityRules = newStabilityRules?.filter(item => !item.id) || []; // a new resource is one which doesn't have an ID
    const deletedStabilityRules = prevMedicationStabilityRules?.filter(item => !updatedStabilityRulesIds.has(item.stabilityRuleId)) || [];
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    if (addedStabilityRules.length) {
        await Promise.all(addedStabilityRules.map(rule => createMedicationStabilityRule(tx, { ...rule, medicationProfileId: medicationProfile.id }, { createdAt: timestamp, createdBy: actor })));
    }
    if (updatedStabilityRules.length) {
        await Promise.all(updatedStabilityRules.map(rule => updateMedicationProfileStabilityRule(tx, { ...rule, medicationProfileId: medicationProfile.id }, { updatedAt: timestamp, updatedBy: actor })));
    }
    if (deletedStabilityRules.length) {
        await Promise.all(deletedStabilityRules.map(rule => deleteMedicationProfileStabilityRule(tx, { medicationStabilityRule: rule }, { deletedAt: timestamp, deletedBy: actor })));
    }
}
export async function replaceMedicationProfileReconstitutionDiluents(tx, { medicationProfile, newReconstitutionDiluentsIds, }, userActions) {
    const prevMedProfReconDilIds = new Set(medicationProfile?.allowedReconstitutionDiluents?.map(reconstDiluent => reconstDiluent.solutionDrugId));
    const newMedProfReconDilIds = new Set(newReconstitutionDiluentsIds);
    const addedDiluents = newReconstitutionDiluentsIds?.filter(id => !prevMedProfReconDilIds.has(id)) || [];
    const deletedDiluents = medicationProfile?.allowedReconstitutionDiluents?.filter(item => !newMedProfReconDilIds.has(item.solutionDrugId)) ||
        [];
    if (addedDiluents.length) {
        await Promise.all(addedDiluents.map(solutionDrugId => createMedicationProfileReconstitutionDiluent(tx, {
            medicationProfileId: medicationProfile?.id,
            solutionDrugId,
        }, userActions)));
    }
    if (deletedDiluents.length) {
        await Promise.all(deletedDiluents.map(medicationProfileReconstitutionDiluent => deleteMedicationProfileReconstitutionDiluent(tx, { medicationProfileReconstitutionDiluentId: medicationProfileReconstitutionDiluent.id }, userActions)));
    }
}
//# sourceMappingURL=replace.js.map